import { useState } from 'react'
import clsx from 'clsx'

/**
 * Marca Cima Sport. Los archivos viven en `public/brand/` para que también
 * puedan usarlos las páginas SEO server-rendered del backend (mismo origen).
 *
 * - `color`: fondos claros (landing, tarjetas de login, footer claro).
 * - `white`: fondos de color u oscuros (sidebar y headers de los portales).
 *
 * El logo es intercambiable SIN tocar código: basta dejar el archivo nuevo en
 * `frontend/public/brand/` con el mismo nombre base (`logo`, `logo-white`,
 * `isotipo`, `isotipo-white`) y cualquiera de las extensiones soportadas. El
 * componente prueba las extensiones en orden hasta que una carga. Procedimiento
 * completo en docs/BRAND.md.
 */

/** Extensiones soportadas, en orden de preferencia. */
const EXTENSIONS = ['webp', 'png', 'svg'] as const

/**
 * Qué extensión tiene hoy cada asset en el repo. Es solo una optimización: al
 * empezar por la correcta, no se gasta una petición fallida de sondeo. Si el
 * archivo cambia de formato y este mapa queda desactualizado, `onError`
 * encuentra igual el correcto — por eso no hace falta mantenerlo al día.
 */
const CURRENT: Record<string, (typeof EXTENSIONS)[number]> = {
  logo: 'webp',
  'logo-white': 'svg',
  isotipo: 'svg',
  'isotipo-white': 'svg',
}

/** Extensión ya confirmada por asset: el sondeo ocurre una sola vez por sesión. */
const resolved = new Map<string, string>()

function candidates(name: string): string[] {
  const first = CURRENT[name]
  const order = first ? [first, ...EXTENSIONS.filter((e) => e !== first)] : [...EXTENSIONS]
  return order.map((ext) => `/brand/${name}.${ext}`)
}

interface BrandImageProps {
  name: string
  alt: string
  className?: string
}

function BrandImage({ name, alt, className }: BrandImageProps) {
  const options = candidates(name)
  const [index, setIndex] = useState(() => {
    const hit = resolved.get(name)
    const known = hit ? options.indexOf(hit) : -1
    return known >= 0 ? known : 0
  })
  const src = options[index]

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onLoad={() => resolved.set(name, src)}
      // Si el archivo no existe, nginx devuelve el index.html de la SPA: la
      // imagen no decodifica y caemos a la extensión siguiente.
      onError={() => setIndex((i) => Math.min(i + 1, options.length - 1))}
    />
  )
}

interface BrandProps {
  variant?: 'color' | 'white'
  className?: string
}

export function Logo({ variant = 'color', className }: BrandProps) {
  return (
    <BrandImage
      name={variant === 'white' ? 'logo-white' : 'logo'}
      alt="Cima Sport"
      className={clsx('w-auto', className ?? 'h-9')}
    />
  )
}

export function Isotipo({ variant = 'color', className }: BrandProps) {
  return (
    <BrandImage
      name={variant === 'white' ? 'isotipo-white' : 'isotipo'}
      alt="Cima Sport"
      className={clsx(className ?? 'h-8 w-8')}
    />
  )
}
